import {
  Accordion,
  AccordionSummary,
  Typography,
  Chip,
  AccordionDetails,
  Grid,
  Box,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { useAppDispatch } from "../../../store/store";
import { Circle, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import { getBackgroundColor } from "../../../core/utils/utils";
import CommonNoRecordMsg from "../../../components/ui/CommonNoRecordMsg";
import { setAudioTimeStampData } from "../../../reduce-slice/AuditSlice";
import { useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";

// Component to render each row of the Call Review Table.
const CallReviewAccordionItem = ({
  row,
  isEditMode,
  onValueChange,
  manualValues,
}: any) => {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<any>(null);
  const questionValue = manualValues[row.internal_id] || row.value;
  const dispatch = useAppDispatch();
  const { manualReviewData, audioTimeStampData } = useSelector(
    (state: any) => state.auditSlice
  );
  // Toggle between "yes" "no" and "n/a" values when in edit mode.
  const handleChipClick = () => {
    if (isEditMode) {
      const newValue =
        questionValue === "yes" ? "no" : questionValue === "no" ? "n/a" : "yes";
      if (onValueChange) {
        onValueChange(row.internal_id, newValue);
      }
    }
  };

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };
  const convertToSeconds = (timeString: string): number => {
    return moment.duration(timeString).asSeconds();
  };
  const handlePlay = (startTime: any, index: any) => {
    dispatch(
      setAudioTimeStampData({
        startTimestamp: convertToSeconds(startTime),
        shouldPlay:
          activeQuestionIndex !== index ? true : !audioTimeStampData.shouldPlay,
      })
    );
    setActiveQuestionIndex(activeQuestionIndex === index ? null : index);
  };

  const questions =
    manualReviewData?.find(
      (data: any) =>
        data.template_category?.internal_id ===
        row?.template_category?.internal_id
    )?.questions ?? [];
  return (
    <div className="accordion-block call-review-accordion">
      <Accordion expanded={expanded}>
        <AccordionSummary
          data-testid="expandTestId"
          expandIcon={
            <div className="chevron-box">
              <IconButton className="p-0" onClick={handleExpandClick}>
                <ExpandMoreIcon />
              </IconButton>
            </div>
          }
          sx={{
            cursor: "default",
            background: "#FCFCFC",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={3}>
              <Typography variant="body2">
                {row.template_category?.name ?? "-"}
              </Typography>
            </Grid>

            <Grid item xs={3}>
              <Box className="d-flex call-review-chip">
                {isEditMode ? (
                  // Display one chip that toggles between "Yes", "No", and "N/A".
                  <Chip
                    label={
                      questionValue === "yes"
                        ? "Yes"
                        : questionValue === "no"
                        ? "No"
                        : "N/A"
                    }
                    className={
                      questionValue === "yes"
                        ? "green-chip"
                        : questionValue === "no"
                        ? "red-chip"
                        : "gray-chip" // Add a CSS class for N/A
                    }
                    onClick={handleChipClick}
                    sx={{
                      marginRight: 1,
                      height: "auto",
                      padding: "4px 8px",
                    }}
                    icon={<Circle />}
                  />
                ) : (
                  // Display the current value in non-edit mode.
                  <Box display="flex" justifyContent="flex-start">
                    <Box sx={{ width: "100px", marginRight: 2 }}>
                      {row?.value ? (
                        <Chip
                          label={
                            row?.value.charAt(0).toUpperCase() +
                            row?.value.slice(1)
                          }
                          className={`${
                            row?.value === "yes"
                              ? "success-chip-label"
                              : row?.value === "no"
                              ? "failed-chip-label"
                              : "gray-chip"
                          }`}
                          sx={{
                            marginRight: 1,
                            height: "auto",
                            padding: "4px 8px",
                            borderRadius: "30px",
                          }}
                          icon={isEditMode ? <Circle /> : undefined}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "auto",
                            padding: "4px 8px",
                            width: "100%",
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ width: "100px" }}>
                      {row?.category_evaluation_score?.value ? (
                        <>
                          <Chip
                            label={
                              row.category_evaluation_score.value
                                ?.charAt(0)
                                ?.toUpperCase() +
                              row.category_evaluation_score.value?.slice(1)
                            }
                            className={`${
                              row.category_evaluation_score.value === "yes"
                                ? "success-chip-label"
                                : row.category_evaluation_score.value === "no"
                                ? "failed-chip-label"
                                : "gray-chip"
                            }`}
                            sx={{
                              marginRight: 1,
                              height: "auto",
                              padding: "4px 8px",
                            }}
                            icon={isEditMode ? <Circle /> : undefined}
                            color="default"
                          />
                        </>
                      ) : (
                        <Box
                          sx={{
                            height: "auto",
                            padding: "4px 8px",
                            width: "100%",
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ width: "100px" }}>
                      {row?.template_category?.auto_failed ? (
                        <CheckCircleOutlineIcon className="failed-check" />
                      ) : (
                        ""
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            background: "#FCFCFC",
          }}
        >
          <Grid container spacing={2}>
            {questions?.length > 0 &&
              questions.map((detail: any, index: any) => {
                return (
                  <Grid item key={index} xs={12} className="py-1">
                    <Box
                      className={`sub-questions-div
                         ${getBackgroundColor(
                           detail?.category_question_evaluation_scores?.value
                         )}
                      `}
                    >
                      <Grid container spacing={2}>
                        <Grid item xs={6} md={10}>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              marginBottom={1}
                            >
                              {`Question ${index + 1}:`}
                              {
                                detail?.template_category_question
                                  ?.question_text
                              }
                            </Typography>
                            <Typography variant="subtitle2" color={"#43434380"}>
                              Answer:
                              {
                                detail?.category_question_evaluation_scores
                                  ?.evaluation_key_word
                              }
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <Box className={"play-icon-call-review-table"}>
                            {detail?.category_question_evaluation_scores
                              ?.start_time &&
                            detail?.category_question_evaluation_scores
                              ?.start_time !== "00:00:00" ? (
                              <IconButton
                                className=""
                                size="small"
                                onClick={() =>
                                  handlePlay(
                                    detail?.category_question_evaluation_scores
                                      ?.start_time,
                                    index
                                  )
                                }
                              >
                                {activeQuestionIndex === index ? (
                                  <PauseCircleFilledIcon
                                    color="primary"
                                    fontSize="large"
                                  />
                                ) : (
                                  <PlayCircleFilledWhiteIcon
                                    color="primary"
                                    fontSize="large"
                                  />
                                )}
                              </IconButton>
                            ) : (
                              <PlayCircleFilledWhiteIcon
                                color="disabled"
                                fontSize="large"
                              />
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                );
              })}

            {questions?.length === 0 && (
              <Grid item xs={12}>
                <CommonNoRecordMsg
                  msg="No questions available."
                  className="text-center"
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
export default CallReviewAccordionItem;
